import { create } from "zustand";
import {
  executeOperation,
  getOperationObjectIds,
  invertOperation,
  type TreeOperation,
} from "./treeOperations";

const MAX_HISTORY_ENTRIES = 50;

export type TreeHistoryEntry = {
  operation: TreeOperation;
  timestamp: Date;
};

type TreeHistoryState = {
  history: TreeHistoryEntry[];
  historyIndex: number;
};

type TreeHistoryActions = {
  push: (operation: TreeOperation) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearEntriesFor: (ids: Set<string>) => void;
  clear: () => void;
};

export type TreeHistoryStore = TreeHistoryState & TreeHistoryActions;

export const useTreeHistoryStore = create<TreeHistoryStore>((set, get) => ({
  history: [],
  historyIndex: -1,

  push: (operation: TreeOperation) => {
    set((state) => {
      // Discard any entries after current position (losing redo stack)
      const history = state.history.slice(0, state.historyIndex + 1);

      // Add new entry
      history.push({
        operation,
        timestamp: new Date(),
      });

      // Trim to max size
      const trimmed =
        history.length > MAX_HISTORY_ENTRIES
          ? history.slice(history.length - MAX_HISTORY_ENTRIES)
          : history;

      return {
        history: trimmed,
        historyIndex: trimmed.length - 1,
      };
    });
  },

  undo: async () => {
    const state = get();

    if (!get().canUndo()) {
      return;
    }

    const entry = state.history[state.historyIndex];
    const inverse = invertOperation(entry.operation);

    await executeOperation(inverse);

    set((s) => ({
      historyIndex: s.historyIndex - 1,
    }));
  },

  redo: async () => {
    const state = get();

    if (!get().canRedo()) {
      return;
    }

    const entry = state.history[state.historyIndex + 1];

    await executeOperation(entry.operation);

    set((s) => ({
      historyIndex: s.historyIndex + 1,
    }));
  },

  canUndo: () => {
    const state = get();
    return state.historyIndex >= 0;
  },

  canRedo: () => {
    const state = get();
    return state.historyIndex < state.history.length - 1;
  },

  clearEntriesFor: (ids: Set<string>) => {
    set((state) => {
      const history = state.history.filter((entry) => {
        const objectIds = getOperationObjectIds(entry.operation);
        return !objectIds.some((id) => ids.has(id));
      });

      // Recalculate historyIndex based on how many entries were removed
      let newIndex = -1;

      if (state.historyIndex >= 0) {
        const currentEntry = state.history[state.historyIndex];
        const currentObjectIds = getOperationObjectIds(currentEntry.operation);
        const currentWasRemoved = currentObjectIds.some((id) => ids.has(id));

        if (currentWasRemoved) {
          // Find the last valid entry before the removed one
          for (let i = state.historyIndex - 1; i >= 0; i--) {
            const entryIds = getOperationObjectIds(state.history[i].operation);
            if (!entryIds.some((id) => ids.has(id))) {
              // Find where this entry ended up in the filtered array
              newIndex = history.findIndex(
                (e) => e.timestamp === state.history[i].timestamp,
              );
              break;
            }
          }
        } else {
          // Current entry still exists, find its new position
          newIndex = history.findIndex(
            (e) => e.timestamp === currentEntry.timestamp,
          );
        }
      }

      return {
        history,
        historyIndex: newIndex,
      };
    });
  },

  clear: () => {
    set({
      history: [],
      historyIndex: -1,
    });
  },
}));
